/**
 * Cache of minted partner-impersonation tokens.
 *
 * Deliberately a leaf module with NO imports. The auth store has to clear this on
 * logout, and reaching into `tattle-http` to do that is not viable: a static import
 * drags the interceptor/router graph into every auth-store consumer, and a dynamic
 * one either races worker teardown (`write EPIPE`) or blocks logout waiting on that
 * graph. Keeping the cache dependency-free lets both sides import it directly.
 */

type CachedToken = { token: string; expiresAt: number }

/**
 * Keyed by `partnerRef:merchantId:scopes` — a token is scoped to a single merchant,
 * and may carry a narrower scope set than the partner holds.
 *
 * The scopes belong in the key: without them, a token minted for the partner's full
 * scopes would be reused for a deliberately narrowed test, and the test would pass
 * on permissions it was meant to be missing.
 */
const tokens = new Map<string, CachedToken>()

/**
 * Mints that have been started but have not resolved yet, same key as `tokens`.
 *
 * `tokens` only records mints that finished, so without this a second caller
 * arriving during the round trip sees a miss and mints again. Two independent
 * watchers reaching the same partner/merchant in one tick is the ordinary case,
 * not a rare one: both consoles auto-select the partner when a merchant has
 * exactly one, which starts the partner watcher while the merchant watcher is
 * still running.
 */
const inFlight = new Map<string, Promise<string>>()

/** Re-mint this far before real expiry so a token can't lapse mid-flight. */
export const IMPERSONATION_REFRESH_SKEW_MS = 30_000

/**
 * Sorted, so the same set of scopes picked in a different order hits the same entry.
 *
 * `undefined` (the partner's full set) is `*`, and an empty array — a token carrying no
 * scopes at all — is the empty string. They are different tokens and must not share a key.
 */
const keyFor = (partnerRef: string, merchantId: number, scopes?: readonly string[]) =>
  `${partnerRef}:${merchantId}:${scopes === undefined ? '*' : [...scopes].sort().join(',')}`

/**
 * The cached token for this partner/merchant, or null when absent or close
 * enough to expiry that it should be re-minted.
 */
export function getPartnerImpersonationToken(
  partnerRef: string,
  merchantId: number,
  scopes?: readonly string[]
): string | null {
  const cached = tokens.get(keyFor(partnerRef, merchantId, scopes))
  if (!cached) return null
  if (cached.expiresAt - IMPERSONATION_REFRESH_SKEW_MS <= Date.now()) return null
  return cached.token
}

export function setPartnerImpersonationToken(
  partnerRef: string,
  merchantId: number,
  token: string,
  expiresAt: string,
  scopes?: readonly string[]
): void {
  tokens.set(keyFor(partnerRef, merchantId, scopes), {
    token,
    expiresAt: new Date(expiresAt).getTime()
  })
}

/**
 * Run `mint` unless one is already in flight for this partner/merchant, in which
 * case join that one instead.
 *
 * Takes the mint as a callback rather than exposing the map, so the entry cannot
 * outlive the request that owns it — a caller that forgot to clean up after a
 * rejection would wedge every later attempt behind a promise that already failed.
 */
export function mintOnce(
  partnerRef: string,
  merchantId: number,
  mint: () => Promise<string>,
  scopes?: readonly string[]
): Promise<string> {
  const key = keyFor(partnerRef, merchantId, scopes)

  const pending = inFlight.get(key)
  if (pending) return pending

  const started: Promise<string> = mint().finally(() => {
    // Only clear our own entry: a later mint may already have replaced it.
    if (inFlight.get(key) === started) inFlight.delete(key)
  })

  inFlight.set(key, started)

  return started
}

/**
 * Drop every cached token. Called on logout — a minted token outlives the session
 * that produced it and must not carry over to whoever logs in next.
 *
 * In-flight mints go too: one started by the outgoing admin would otherwise be
 * joined by the next one to log in.
 */
export function clearPartnerImpersonationTokens(): void {
  tokens.clear()
  inFlight.clear()
}
